/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * Expenses Analyzer Layout with Sidebar
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @version 1.16.0
 * @since 1.1.4
 */

import { ReactNode, useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { SideBar, SideBarNav } from '../../../components/common';
import { SIDE_BAR_SELECT_ALL_ITEM } from '../../../components/common/SideBar/SideBarConstants';
import { ISideBarItem } from '../../../components/common/SideBar/types/SideBarTypes';
import { IPageExpensesAnalyzerTranslation } from '../../../modules/internationalization/types';
import { IRootReducer } from '../../../reducers/rootReducer';
import {
    makeSelectorExpensesAccountsList,
    makeSelectorExpensesAccountsListFulled,
} from '../pageExpensesAnalyzerSelectors';
import { IExpensesAccountsListItem } from '../types/pageExpensesAnalyzerTypes';

export interface IExpensesAnalyzerLayoutProps {
    children: ReactNode;
    getExpensesAccountsList: (payload?: boolean) => void;
    isTableAccount: boolean;
    selectAccountHandler: (account: IExpensesAccountsListItem) => void;
    selectedId: number;
    sideBarMarginBottom: number;
    translation: IPageExpensesAnalyzerTranslation;
}

export type TExpensesAnalyzerLayout = IExpensesAnalyzerLayoutProps & IConnectedProps;

export const ExpensesAnalyzerLayout = (props: TExpensesAnalyzerLayout) => {
    const [inited, setInited] = useState<boolean>(false);
    const [sideBarItems, setSideBarItems] = useState<ISideBarItem[]>([]);

    useEffect(() => {
        setInited(true);
        setSideBarItems(props.accountsList.map(item => ({
            label: item.account,
            value: item.clntId.toString(),
        })));
    }, [props.accountsList]);

    const sideBarWidth = props.isTableAccount ? 200 : 0;

    const getExpensesAccountsList = () => {
        if (inited && !props.accountsListFulled) {
            props.getExpensesAccountsList();
        }
    };

    return <div>

        <SideBar
            hasMore={!props.accountsListFulled}
            loadMore={getExpensesAccountsList}
            minHeight={508}
            navMarginBottom={props.sideBarMarginBottom}
            navWidth={sideBarWidth}
        >
            {
                /* children[0] */
                <SideBarNav
                    activeItemValue={props.selectedId.toString()}
                    firstItem={{
                        value: SIDE_BAR_SELECT_ALL_ITEM.toString(),
                        label: props.translation.selectAll,
                    }}
                    items={sideBarItems}
                    onClick={(item) => props.selectAccountHandler({
                        account: item.label,
                        clntId: parseInt(item.value),
                    })}
                >
                </SideBarNav>
            }

            {
                /* children[1] */
                props.children
            }

        </SideBar>

    </div>;
};

interface IConnectedProps {
    accountsList: IExpensesAccountsListItem[];
    accountsListFulled: boolean;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    accountsList: makeSelectorExpensesAccountsList(),
    accountsListFulled: makeSelectorExpensesAccountsListFulled(),
});

export default connect(mapStateToProps)(ExpensesAnalyzerLayout);
